import { api_configuration } from "../../common";
import api_application = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import { addMessage } from "../front/messages";
import { DatastoreProxy } from "../interface/DatastoreProxy";

export function isPasswordProperty(text_property: api_application.Ctext__type__property): boolean {
	const application_model = text_property.root;
	return application_model.properties.has_dynamic_users.switch({
		yes: ($) =>
			$.properties.has_password_authentication.switch({
				yes: ($) => text_property === $.properties.password_text_path.inferences.text(),
				no: false,
			}),
		no: false,
	});
}

export function resetPassword(text_property: api_client_state.Ctext__type__property): void {
	if (isPasswordProperty(text_property.inferences.text_type())) {
		const password_entry = text_property.parent.parent.parent.input.entity().cast("entry");
		text_property.properties.is_editable.switch({
			no: () => {
				addMessage("warning", "No permission to reset password.");
			},
			yes: (editable) => {
				password_entry.properties.key.ref.properties.backend_known.switch({
					no: () => {
						addMessage("warning", "Can not reset password.", { body: "Username is unknown." });
					},
					yes: async (backend_known) => {
						const username = backend_known.properties.value;
						const result = await DatastoreProxy.instance.resetPassword(username);
						result.properties.user_context.switch({
							"does not exist": () => {
								addMessage("warning", "Could not reset password");
							},
							"permission denied": () => {
								addMessage("warning", "Could not reset password");
							},
							exists: (existing_user_context) =>
								existing_user_context.properties.result.switch({
									success: (success) => {
										const temporary_password = success.properties.temporary_password;
										const mutable_password = text_property;
										mutable_password.properties.backend_known.set("yes", {
											value: temporary_password,
										});
										mutable_password.properties.is_set.set("yes", {
											text: temporary_password,
											by: ["engine", {}],
										});
										editable.properties.modified.set("no", {});
									},
									failure: () => {
										addMessage("warning", "Could not reset password");
									},
								}),
						});
					},
				});
			},
		});
	} else {
		addMessage("warning", "Could not reset password");
	}
}

function getPasswordToken(
	text_property: api_client_state.Ctext__type__property,
	include_source: boolean,
	callback: (reset_link: string) => void
) {
	const collection_entry = text_property.parent.parent.parent.input.entity().cast("entry");
	if (isPasswordProperty(text_property.inferences.text_type())) {
		collection_entry.properties.key.ref.properties.backend_known.switch({
			no: () => {
				addMessage("warning", "Can not send password", { body: "Username is unknown." });
			},
			yes: (user_known) => {
				text_property.properties.backend_known.switch({
					no: () => {
						addMessage("warning", "Can not send password", { body: "Reset the password." });
					},
					yes: (password_token_known) => {
						DatastoreProxy.instance.connection.getAuthenticationUri().then((uri) => {
							const params = new URLSearchParams({
								action: "reset",
								uname: user_known.properties.value,
								token: password_token_known.properties.value,
							});
							if (include_source) {
								params.append("source", window.location.href);
								// params.append("symnam": ""); //TODO: this should be set to the 'application name' from the settings
							}
							const reset_uri = uri + "#" + params.toString();
							callback(reset_uri);
						});
					},
				});
			},
		});
	}
}

export function mailPasswordToken(text_property: api_client_state.Ctext__type__property): void {
	getPasswordToken(text_property, false, (uri) => {
		window.open("mailto:?subject=password reset&body=" + encodeURIComponent(uri), "_blank");
	});
}

// Type declarations for Clipboard API
// https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API
interface Clipboard {
	writeText(newClipText: string): Promise<void>;
}

interface NavigatorClipboard {
	readonly clipboard?: Clipboard;
}

type Navigator = NavigatorClipboard;

export function copyPasswordTokenToClipboard(text_property: api_client_state.Ctext__type__property): void {
	const clipboard = (navigator as Navigator).clipboard;
	if (clipboard) {
		getPasswordToken(text_property, true, (uri) => {
			clipboard.writeText(uri).then(
				function () {
					addMessage("success", "Password reset link copied to clipboard", { key: "Password copied to clipboard" });
				},
				function () {
					addMessage("warning", "Can't copy password", { body: "Failed to write to the clipboard." });
				}
			);
		});
	} else {
		addMessage("warning", "Can't copy password", { body: "Clipboard is not supported." });
	}
}
